import React from 'react';
import { DropdownItem, Button as UIButton, UncontrolledTooltip } from 'reactstrap';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Toolkit } from '../../../../services/types/Toolkit';
import { CustomDropDown, Button } from '../../../../shared/components';
import CheckboxList from './CheckboxList';
import { CheckValue } from '../../../../services/types/Misc';
import ImpactBadge from '../../ImpactBadge';
import { Operation } from '../../../../services/types/Operation';
import RiskBadge from '../../RiskBadge';
import { dateTimeFormat, tooltipPreventOverflowModifiers } from '../../../../shared/misc/utils';

interface Props {
  operation: Operation;
  toolkit: Toolkit;
  pdfOptions?: CheckValue[];
  handleChange: (id: string, value: boolean) => void;
  handleDownloadClick: (toolkitId: number) => void;
  handleDownloadClickEzshare: (ezShareCode: string) => void;
}

const ToolkitVersion = (props: Props) => {
  const {
    operation,
    toolkit,
    pdfOptions,
    handleChange,
    handleDownloadClick,
    handleDownloadClickEzshare,
  } = props;
  const { t } = useTranslation();
  const isLegacy = toolkit.source === 'Legacy_Convergence';
  return (
    <tr>
      <td>
        {(() => {
          if (toolkit.isDeleted) {
            return (
              <span className="deleted-toolkit">
                <FontAwesomeIcon icon="copy" />
                {` Toolkit v.${toolkit.version}`}
                <div className="ml-2 toolkit-tooltip" id={`tr-toolkit-version-${toolkit.version}`}>
                  <FontAwesomeIcon icon="exclamation-circle" />
                </div>
                <UncontrolledTooltip
                  className="tooltip-toolkit"
                  placement="bottom"
                  target={`tr-toolkit-version-${toolkit.version}`}
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  {t('documentDeleted')}
                </UncontrolledTooltip>
              </span>
            );
          }
          if (isLegacy) {
            return (
              <span className="deleted-toolkit deleted-toolkit-text">
                <FontAwesomeIcon icon="copy" />
                {` Toolkit v.${toolkit.version}`}
                <span className="ml-2 toolkit-tooltip" id={`tr-toolkit-id-${toolkit.id}`}>
                  <FontAwesomeIcon icon="exclamation-circle" className="lastUpdateInfo" />
                </span>
                <UncontrolledTooltip
                  className="tooltip-toolkit"
                  placement="bottom"
                  target={`tr-toolkit-id-${toolkit.id}`}
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  {t('toolkitMigratedMsg')}
                </UncontrolledTooltip>
              </span>
            );
          }
          return (
            <Link
              key={`tv_link_to_${toolkit.id}`}
              to={`/operations/${operation.number}/toolkit/form/${toolkit.id}`}
              id={`tv_link_to_${toolkit.id}`}
            >
              <span>
                <FontAwesomeIcon icon="copy" />
                {` Toolkit v.${toolkit.version}`}
              </span>
            </Link>
          );
        })()}
      </td>
      <td>
        <div className="impactCategory-info">
          <ImpactBadge text={toolkit.impactCategory || toolkit.impactCategoryCalculated} />
          {toolkit.impactCategory &&
            toolkit.impactCategoryCalculated &&
            toolkit.impactCategory !== toolkit.impactCategoryCalculated && (
              <>
                <span id={`impactCategory_icon_${toolkit.id}`} className="impactCategory-info_icon">
                  <FontAwesomeIcon icon="info-circle" />
                </span>
                <span>Changed</span>
                <UncontrolledTooltip
                  className="tooltip-impact"
                  placement="bottom"
                  target={`impactCategory_icon_${toolkit.id}`}
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  <p>
                    <b>
                      {t('categoryChangedTooltip', {
                        catCalculated: toolkit.impactCategoryCalculated,
                        cat: toolkit.impactCategory,
                        by: toolkit.pdfCreatedBy,
                      })}
                    </b>
                  </p>
                  <p>
                    {t('rationale')}: <i>{toolkit.rationale}</i>
                  </p>
                  <p>
                    {t('comments')}: <i>{toolkit.rationalComments}</i>
                  </p>
                </UncontrolledTooltip>
              </>
            )}
        </div>
      </td>
      <td>
        <div className="impactCategory-info">
          <RiskBadge text={toolkit.disasterRisk || toolkit.disasterRiskCalculated} />
          {toolkit.disasterRisk &&
            toolkit.disasterRiskCalculated &&
            toolkit.disasterRisk !== toolkit.disasterRiskCalculated && (
              <>
                <span id={`disasterRisk_icon_${toolkit.id}`} className="impactCategory-info_icon">
                  <FontAwesomeIcon icon="info-circle" />
                </span>
                <span>Changed</span>
                <UncontrolledTooltip
                  className="tooltip-impact"
                  placement="bottom"
                  target={`disasterRisk_icon_${toolkit.id}`}
                  modifiers={tooltipPreventOverflowModifiers}
                >
                  <p>
                    <b>
                      {t('categoryChangedTooltip', {
                        catCalculated: toolkit.disasterRiskCalculated,
                        cat: toolkit.disasterRisk,
                        by: toolkit.pdfCreatedBy,
                      })}
                    </b>
                  </p>
                  <p>
                    {t('rationale')}: <i>{toolkit.disasterRiskRationale}</i>
                  </p>
                  <p>
                    {t('comments')}: <i>{toolkit.disasterRiskRationaleComments}</i>
                  </p>
                </UncontrolledTooltip>
              </>
            )}
        </div>
      </td>
      <td>{toolkit.createdBy}</td>
      <td>{dateTimeFormat(toolkit.modifiedOn)}</td>
      <td>
        {(() => {
          if (pdfOptions && !isLegacy) {
            return (
              <>
                {pdfOptions.length > 1 ? (
                  <CustomDropDown
                    key={`tv_dd_download_${toolkit.id}`}
                    text={t('downloadSFs')}
                    icon="file-download"
                    className="dropdown_tertiary"
                    outline={false}
                    color="tertiary"
                  >
                    <div className="dropdown-content">
                      <CheckboxList checkValues={pdfOptions} handleChange={handleChange} />
                    </div>
                    <DropdownItem divider />
                    <div className="dropdown-button">
                      <UIButton
                        key={`tv_btn_download_${toolkit.id}`}
                        color="primary"
                        block
                        onClick={() => handleDownloadClick(toolkit.id)}
                      >
                        Download
                      </UIButton>
                    </div>
                  </CustomDropDown>
                ) : (
                  <Button
                    key={`tv_btn_download_${toolkit.id}`}
                    className="btn-tertiary no-padding"
                    htmlId={`tv_btn_download_${toolkit.id}`}
                    icon="file-download"
                    text={t('downloadSF')}
                    onClick={() => handleDownloadClick(toolkit.id)}
                  />
                )}
              </>
            );
          }
          if (isLegacy) {
            return (
              <>
                {toolkit.ezSPFCode !== '' && (
                  <Button
                    key={`tv_btn_download_sf_${toolkit.id}`}
                    className="btn-tertiary no-padding"
                    htmlId={`tv_btn_download_sf_${toolkit.id}`}
                    icon="file-download"
                    text={t('downloadSPF')}
                    onClick={() => handleDownloadClickEzshare(toolkit.ezSPFCode)}
                  />
                )}
                {toolkit.ezSSFCode !== '' && (
                  <Button
                    key={`tv_btn_download_ssf_${toolkit.id}`}
                    className="btn-tertiary no-padding"
                    htmlId={`tv_btn_download_ssf_${toolkit.id}`}
                    icon="file-download"
                    text={t('downloadSSF')}
                    onClick={() => handleDownloadClickEzshare(toolkit.ezSSFCode)}
                  />
                )}
              </>
            );
          }
          return <div>-</div>;
        })()}
      </td>
    </tr>
  );
};

export default ToolkitVersion;
